import { html, type TemplateResult } from 'lit';
import { termDefinitions, type TermDefinition } from './terms';

/**
 * Protects a `casing: 'preserve'` term from a CSS text transform applied by its surroundings.
 *
 * THE DEFECT THIS EXISTS FOR, found on the company list on 2026-08-22. The glossary produced
 * `iMIS` correctly and `casing: 'preserve'` did its job, but the column header carries
 * `text-transform: uppercase` (`data-table.styles.css:115`), so the word reached the screen as
 * "IMIS" - which this repository has a standing rule against. Every existing guard passed: the term
 * was right, the compiler was happy, and `terms.spec.ts` stayed green, because all of them inspect
 * the STRING and the damage happens after it, in the renderer.
 *
 * `casing: 'preserve'` means the capitalisation IS the word's identity, so any transform that
 * rewrites it produces a different word. Lower-casing was already prevented, by the policy; this
 * closes the other direction.
 *
 * Uses an inline style rather than a class deliberately: the returned template is authored in one
 * component and rendered inside ANOTHER component's shadow root (a data-table column title is the
 * motivating case), where a class from the author's stylesheet does not reach. An inline style
 * travels with the element.
 */

/**
 * The authored values of every preserve-cased term, longest first.
 *
 * Longest first so that a term which is a prefix of another cannot claim the match and leave the
 * remainder unprotected. Derived from `termDefinitions` rather than listed here, so a new
 * preserve-cased term is covered the moment it is authored - the same reason nothing else in this
 * system is authored twice.
 */
// Widened to the declared type on the way in: `termDefinitions` is `as const satisfies`, so its
// inferred type is a union in which only the preserve-cased members carry a `casing` property at
// all, and reading it off the union does not compile. The authored contract is TermDefinition.
const preservedValues: readonly string[] = (
  Object.values(termDefinitions) as TermDefinition[]
)
  .filter((definition) => definition.casing === 'preserve')
  .flatMap((definition) => [definition.value, definition.plural])
  .filter((value, index, all) => all.indexOf(value) === index)
  .sort((a, b) => b.length - a.length);

/**
 * Splits text on preserve-cased terms and wraps each one so no transform can rewrite it.
 *
 * Returns a template rather than a string because protection requires an element to hang the style
 * on. Text containing no preserve-cased term still returns a template, so callers do not have to
 * branch on it, and the surrounding style continues to apply to everything that is not a term.
 */
export function preservedCasing(text: string): TemplateResult {
  if (preservedValues.length === 0) return html`${text}`;

  // One alternation of every preserved value, with a capturing group so `split` keeps the matches.
  // Escaped because a term value is authored text and may legitimately contain regex punctuation.
  const pattern = new RegExp(
    `(${preservedValues.map(escapeForRegex).join('|')})`,
    'g'
  );

  const segments = text.split(pattern);
  return html`${segments.map((segment) =>
    preservedValues.includes(segment)
      ? html`<span style="text-transform: none">${segment}</span>`
      : segment
  )}`;
}

/** Escapes regex metacharacters so a term value is matched literally. */
function escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
